/**
 * /marketing/lists — the audience half of Marketing.
 *
 * Two tabs, `?tab=` in the URL with the default (lists) spelled by the key's
 * ABSENCE: nothing is written on arrival, and switching back to Lists deletes
 * the key rather than writing `tab=lists`.
 *
 * ⚠️ A segment's member count is a READING taken when it was last counted,
 * not a live figure — `memberCount: null` is "never counted" and is never
 * rendered as zero.
 */
import { useEffect, useState } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  SegmentedControl,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { NewListModal } from '@/components/marketing/lists/NewListModal';
import { Pager } from '@/components/marketing/lists/ListMembersTable';
import { useDebounced } from '@/components/marketing/lists/ref-options';
import { summariseSegment } from '@/components/marketing/lists/segment-summary';
import { SubscribersTable } from '@/components/marketing/lists/SubscribersTable';
import { api, ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import type {
  MarketingList,
  MarketingListAudience,
  MarketingListKind,
  SegmentField,
  SegmentFieldCatalogue,
} from '@/lib/types';

type Tab = 'lists' | 'subscribers';

const PER_PAGE = 25;

const AUDIENCE_LABEL: Record<MarketingListAudience, string> = {
  contacts: 'Contacts',
  subscribers: 'Subscribers',
  mixed: 'Contacts and subscribers',
};

const KIND_LABEL: Record<MarketingListKind, string> = {
  segment: 'Live',
  static: 'Static',
};

export function ListsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get('tab') === 'subscribers' ? 'subscribers' : 'lists';
  const { ability } = useAuth();
  const canRead = ability.canRead('marketing-lists');
  const canCreate = ability.canCreate('marketing-lists');
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'lists') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  if (!canRead) {
    return (
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
        <PageHeader eyebrow="B2B Marketing" title="Lists" />
        <EmptyState
          title="Lists are restricted"
          description="Your permission set does not include this. An administrator can change that."
        />
      </div>
    );
  }

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6">
      <PageHeader
        eyebrow="B2B Marketing"
        title="Lists"
        actions={
          tab === 'lists' && canCreate ? <Button onClick={() => setNewOpen(true)}>New list</Button> : undefined
        }
      />

      <SegmentedControl
        label="Lists section"
        value={tab}
        options={[
          { value: 'lists', label: 'Lists' },
          { value: 'subscribers', label: 'Subscribers' },
        ]}
        onChange={setTab}
        className="max-w-full self-start"
      />

      {tab === 'lists' ? <ListsTab /> : <SubscribersTable />}

      <NewListModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(list) => {
          setNewOpen(false);
          // `?new=1` tells the detail page a SEGMENT was just made, so it seeds
          // one blank rule into the draft and then drops the key.
          navigate(`/marketing/lists/${list.id}${list.kind === 'segment' ? '?new=1' : ''}`);
        }}
      />
    </div>
  );
}

type ArchivedFilter = 'active' | 'archived';

function ListsTab() {
  const { ability } = useAuth();
  const canCreate = ability.canCreate('marketing-lists');
  const canUpdate = ability.canUpdate('marketing-lists');
  const canDelete = ability.canDelete('marketing-lists');
  const navigate = useNavigate();

  const [search, setSearch] = useRememberedState('lists-search', '');
  const debounced = useDebounced(search.trim());
  // Several kinds at once (owner 2026-10-28): a comma-separated list, '' = all.
  // A single kind remembered before reads back as a one-item selection.
  const [kind, setKind] = useRememberedState<string>('lists-kind', '', (raw) =>
    typeof raw === 'string' &&
    raw.split(',').every((part) => part === '' || part === 'segment' || part === 'static')
      ? raw
      : undefined,
  );
  const kinds = kind.split(',').filter((part): part is MarketingListKind => part !== '');
  const [archived, setArchived] = useRememberedState<ArchivedFilter>('lists-archived', 'active', (raw) =>
    raw === 'active' || raw === 'archived' ? raw : undefined,
  );
  const [page, setPage] = useState(1);
  const filterKey = `${debounced}|${kind}|${archived}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (lastFilterKey !== filterKey) {
    setLastFilterKey(filterKey);
    setPage(1);
  }

  const [rows, setRows] = useState<MarketingList[] | null>(null);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<MarketingList | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listMarketingLists(
        {
          page,
          perPage: PER_PAGE,
          archived: archived === 'archived',
          ...(debounced ? { search: debounced } : {}),
          ...(kind ? { kind } : {}),
        },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setRows(response.data);
        setMeta({ total: response.meta.total, totalPages: response.meta.totalPages });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRows(null);
        setError(err instanceof ApiError ? (err.formMessage ?? err.message) : 'Lists could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, debounced, kind, archived, reload]);

  // The field catalogue, read ONCE for every row's rule summary. `undefined`
  // while loading; `null` when it failed, and the summaries then name fields by
  // their key rather than waiting for ever.
  const [catalogue, setCatalogue] = useState<SegmentFieldCatalogue | null | undefined>(undefined);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listSegmentFields(controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setCatalogue(response.data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCatalogue(null);
      });
    return () => controller.abort();
  }, []);

  const fieldsFor = (list: MarketingList): SegmentField[] => {
    if (!catalogue) return [];
    return list.audience === 'subscribers' ? catalogue.subscribers : catalogue.contacts;
  };

  const describe = (err: unknown, fallback: string) =>
    err instanceof ApiError ? (err.formMessage ?? err.message) : fallback;

  const duplicate = async (list: MarketingList) => {
    if (busyId !== null) return;
    setBusyId(list.id);
    setActionError(null);
    try {
      const copy = await api.duplicateMarketingList(list.id);
      navigate(`/marketing/lists/${copy.data.id}`);
    } catch (err) {
      setActionError(describe(err, `${list.name} could not be duplicated.`));
    } finally {
      setBusyId(null);
    }
  };

  const toggleArchive = async (list: MarketingList) => {
    if (busyId !== null) return;
    setBusyId(list.id);
    setActionError(null);
    try {
      await api.updateMarketingList(list.id, { isArchived: !list.isArchived });
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(describe(err, `${list.name} could not be ${list.isArchived ? 'restored' : 'archived'}.`));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteMarketingList(deleting.id);
      setDeleting(null);
      setReload((n) => n + 1);
    } catch (err) {
      setDeleteError(describe(err, 'The list could not be deleted.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const showActions = canCreate || canUpdate || canDelete;
  const filtered = Boolean(debounced) || kind !== '' || archived === 'archived';

  return (
    <Panel>
      <PanelHeader title="Lists" />
      <div className="grid gap-3 border-t border-border px-4 py-3 sm:grid-cols-[minmax(0,20rem)_minmax(0,12rem)_auto] sm:items-center">
        <Input
          type="search"
          aria-label="Search lists"
          placeholder="Search lists"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <MultiCombobox
          values={kinds}
          onChange={(values) => setKind(values.join(','))}
          options={[
            { value: 'segment', label: 'Live' },
            { value: 'static', label: 'Static' },
          ]}
          placeholder="All kinds"
        />
        <SegmentedControl
          label="Show"
          value={archived}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' },
          ]}
          onChange={setArchived}
        />
      </div>

      {actionError && (
        <div className="px-4 pb-3">
          <Notice tone="danger" role="alert">
            {actionError}
          </Notice>
        </div>
      )}

      {error ? (
        <div className="px-4 pb-4">
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        </div>
      ) : loading && rows === null ? (
        <div className="grid gap-2 px-4 pb-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          title={filtered ? 'No lists match' : 'No lists yet'}
          description={
            filtered
              ? 'Try a different search or filter.'
              : canCreate
                ? 'Create a segment or a static list to send campaigns to.'
                : 'Nobody has created a list yet.'
          }
        />
      ) : (
        <TableWrap frozenHeader>
          <Table className="min-w-[1180px]">
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Type</TH>
                <TH>Rules</TH>
                <TH>Audience</TH>
                <TH>Members</TH>
                <TH numeric>Used by</TH>
                <TH>Last counted</TH>
                <TH>Updated</TH>
                {showActions && <TH className="w-[230px]">Actions</TH>}
              </TR>
            </THead>
            <TBody>
              {rows.map((list) => (
                <TR key={list.id}>
                  <TD className="max-w-[18rem]">
                    <div className="grid min-w-0">
                      <TextLink
                        to={`/marketing/lists/${list.id}`}
                        className="truncate font-medium"
                        title={list.name}
                      >
                        {list.name}
                      </TextLink>
                      {list.description && (
                        <span className="truncate text-xs text-fg-muted" title={list.description}>
                          {list.description}
                        </span>
                      )}
                    </div>
                  </TD>
                  <TD>
                    <div className="flex gap-1.5">
                      <Badge tone="neutral" variant="outline">{KIND_LABEL[list.kind]}</Badge>
                      {list.isArchived && <Badge tone="warning">Archived</Badge>}
                    </div>
                  </TD>
                  <TD className="max-w-[20rem]">
                    <RuleSummary list={list} fields={fieldsFor(list)} settled={catalogue !== undefined} />
                  </TD>
                  <TD>{AUDIENCE_LABEL[list.audience]}</TD>
                  <TD>
                    <MemberCount list={list} />
                  </TD>
                  <TD numeric className="whitespace-nowrap">
                    {list.usedByCampaigns === 0
                      ? '—'
                      : `${list.usedByCampaigns} ${list.usedByCampaigns === 1 ? 'campaign' : 'campaigns'}`}
                  </TD>
                  <TD className="whitespace-nowrap">
                    {list.kind === 'static' ? (
                      // A static list's count is its membership, read live — it is
                      // never "counted", so "Never" would be a false statement.
                      <span className="text-fg-muted">—</span>
                    ) : list.lastCountedAt ? (
                      formatDateTime(list.lastCountedAt)
                    ) : (
                      <span className="text-fg-muted">Never</span>
                    )}
                  </TD>
                  <TD className="whitespace-nowrap">{formatDateTime(list.updatedAt)}</TD>
                  {showActions && (
                    <TD>
                      <div className="flex flex-wrap gap-1.5">
                        {canCreate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={busyId === list.id}
                            disabled={busyId !== null && busyId !== list.id}
                            onClick={() => duplicate(list)}
                          >
                            Duplicate
                          </Button>
                        )}
                        {canUpdate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId !== null}
                            onClick={() => toggleArchive(list)}
                          >
                            {list.isArchived ? 'Restore' : 'Archive'}
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId !== null}
                            onClick={() => {
                              setDeleteError(null);
                              setDeleting(list);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </TD>
                  )}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      )}

      {rows && (
        <Pager
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          shown={rows.length}
          noun={{ singular: 'list', plural: 'lists' }}
          onPage={setPage}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => (deleteBusy ? undefined : setDeleting(null))}
        onConfirm={confirmDelete}
        title={deleting ? `Delete ${deleting.name}?` : 'Delete this list?'}
        confirmLabel="Delete list"
        description={
          deleting
            ? `The list will be deleted and can't be recovered. ${
                deleting.kind === 'static'
                  ? 'Its membership goes with it; the contacts and subscribers themselves are kept.'
                  : 'The contacts it matches are not affected.'
              } A list a sent or scheduled campaign uses cannot be deleted — archive it instead.`
            : ''
        }
        loading={deleteBusy}
        error={deleteError}
      />
    </Panel>
  );
}

function MemberCount({ list }: { list: MarketingList }) {
  if (list.memberCount === null) {
    return <span className="text-fg-muted">Not counted</span>;
  }
  return <span className="tabular">{list.memberCount.toLocaleString()}</span>;
}

/**
 * A segment's rules on one line, truncated, with the whole sentence on `title`.
 * Nothing renders until the field catalogue has settled, so a row does not
 * flash raw field keys and then re-word itself.
 */
function RuleSummary({ list, fields, settled }: { list: MarketingList; fields: SegmentField[]; settled: boolean }) {
  if (list.kind !== 'segment') return <span className="text-fg-muted">—</span>;
  if (!settled) return <Skeleton className="h-4 w-40" />;
  const summary = summariseSegment(list.definition, fields);
  return (
    <span className="block truncate text-sm text-fg-secondary" title={summary}>
      {summary}
    </span>
  );
}
